/**
 * Resolves the text of the auto-placed hardcopy note as it actually stands at save time.
 *
 * The note is recorded in the database as well as burned into the hardcopy, and the two must agree.
 * The text typed into the page's "Hardcopy Note" box is only the *starting* value: once the modal is
 * open the user can edit the note in place, or delete it outright and annotate freehand instead.
 * Recording the original box text would file a note that says something the hardcopy does not — the
 * one failure mode that makes the searchable copy worse than having none.
 *
 * So the text is read back off the serialized overlay, keyed by the `annotationId` auto-placement
 * assigned. A deleted note resolves to null and nothing is recorded.
 *
 * Pure, so this can be unit tested without fabric, pdf.js or pdf-lib present.
 */

import { SerializedAnnotation, findAutoPlacedNote } from './annotationPageTurn';

export interface AnnotatedPage {
    objects?: SerializedAnnotation[];
}

/**
 * The current text of the auto-placed note, or null when there is none to record.
 *
 * Pages are scanned in ascending order; in practice auto-placement puts the note on exactly one
 * page, but a page turn can re-place it, so the lookup is per-page rather than assuming page 1.
 */
export function resolveAutoPlacedNoteText(
    pageAnnotations: Record<number, AnnotatedPage | undefined>,
    autoPlacedNoteIds: Record<number, string | null | undefined>
): string | null {
    const pageNumbers = Object.keys(autoPlacedNoteIds)
        .map(Number)
        .filter(pageNumber => Number.isFinite(pageNumber))
        .sort((a, b) => a - b);

    for (const pageNumber of pageNumbers) {
        const noteId = autoPlacedNoteIds[pageNumber];
        if (!noteId) continue;

        const objects = pageAnnotations[pageNumber]?.objects;
        if (!objects) continue;

        // Same test the burn and the page turn apply, so all three resolve to one object.
        const note = findAutoPlacedNote(objects, noteId);
        const text = typeof note?.text === 'string' ? note.text.trim() : '';
        if (text) return text;
    }

    return null;
}
